import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, IndianRupee, Receipt, Search, Users } from "lucide-react";
import { PageHead } from "@/components/Filters";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_STYLES = {
  paid: "bg-emerald-50 text-emerald-800",
  created: "bg-amber-50 text-amber-800",
  failed: "bg-rose-50 text-rose-800",
  amount_mismatch: "bg-rose-50 text-rose-800",
};
const FORBIDDEN_STATUS = 403;

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="border border-slate-200 bg-white p-4" data-testid={testId}>
    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
      <Icon className="h-3.5 w-3.5" /> {label}
    </div>
    <p className="mt-1 font-mono-num text-2xl font-semibold text-[#1A365D]">{value}</p>
  </div>
);

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

export default function AdminPayments() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const qRef = useRef("");
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ paid_count: 0, revenue: 0, orders: 0, flagged: 0, granted: 0 });
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/payments", {
        params: { q: qRef.current || undefined, status: status || undefined },
      });
      setRows(data.items);
      setSummary(data.summary);
      setDenied(false);
    } catch (e) {
      if (e?.response?.status === FORBIDDEN_STATUS) setDenied(true);
      else toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const runSearch = () => {
    qRef.current = q;
    load();
  };

  if (denied)
    return (
      <div data-testid="payments-access-denied" className="border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
        Only the owner account can view payments. You are signed in as {user?.email}.
      </div>
    );

  return (
    <div className="space-y-5" data-testid="admin-payments-page">
      <PageHead title="Payments" subtitle="Every order, completed purchase and flagged payment, newest first." />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={IndianRupee} label="Revenue collected" value={`₹${summary.revenue.toLocaleString("en-IN")}`} testId="stat-revenue" />
        <Stat icon={Receipt} label="Purchases" value={`${summary.paid_count} of ${summary.orders}`} testId="stat-purchases" />
        <Stat icon={AlertTriangle} label="Needs review" value={summary.flagged} testId="stat-flagged" />
        <Stat icon={Users} label="Granted (no payment)" value={summary.granted} testId="stat-granted" />
      </div>

      <div className="flex flex-wrap items-end gap-3 border border-slate-200 bg-white p-4">
        <label className="flex w-full flex-col gap-1 sm:w-auto">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Search</span>
          <div className="flex">
            <input
              data-testid="payment-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runSearch()}
              placeholder="Order id, payment id, name, email or phone"
              className="h-9 w-full border border-slate-300 px-2 text-sm sm:w-80"
            />
            <button
              data-testid="payment-search-btn"
              onClick={runSearch}
              className="flex h-9 items-center gap-1 bg-[#1A365D] px-3 text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#122845]"
            >
              <Search className="h-3.5 w-3.5" /> Find
            </button>
          </div>
        </label>
        <label className="flex w-full flex-col gap-1 sm:w-auto">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Status</span>
          <select
            data-testid="payment-status-filter"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="h-9 w-full border border-slate-300 px-2 text-sm sm:w-auto"
          >
            <option value="">All</option>
            <option value="paid">Paid</option>
            <option value="created">Started, not paid</option>
            <option value="failed">Failed</option>
            <option value="flagged">Needs review</option>
          </select>
        </label>
      </div>

      <div className="overflow-auto border border-slate-200 bg-white">
        <table className="data-table" data-testid="payments-table">
          <thead>
            <tr>
              <th>Student</th>
              <th>Order</th>
              <th className="text-right">Amount</th>
              <th>Status</th>
              <th>Started</th>
              <th>Paid</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="py-6 text-center text-slate-500" data-testid="payments-loading">
                  Loading payments…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="py-6 text-center text-slate-500" data-testid="payments-empty">
                  No payments match this view.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`payment-row-${r.id}`}>
                <td className="max-w-[240px] truncate">
                  <span className="font-medium">{r.user_name || "—"}</span>
                  <span className="block text-xs text-slate-500">{r.user_phone || r.user_email || "unknown"}</span>
                </td>
                <td className="text-xs">
                  <span className="font-mono-num">{r.order_id}</span>
                  {r.payment_id && <span className="block font-mono-num text-slate-500">{r.payment_id}</span>}
                </td>
                <td className="text-right font-mono-num">₹{r.amount.toLocaleString("en-IN")}</td>
                <td>
                  <span
                    data-testid={`payment-status-${r.id}`}
                    className={`px-2 py-0.5 text-xs font-semibold ${STATUS_STYLES[r.status] || "bg-slate-100 text-slate-600"}`}
                  >
                    {r.status === "created" ? "Not completed" : r.status === "amount_mismatch" ? "Amount mismatch" : r.status}
                  </span>
                  {r.needs_review && (
                    <span className="ml-1 bg-rose-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
                      Review
                    </span>
                  )}
                </td>
                <td className="text-xs text-slate-500">{when(r.created_at)}</td>
                <td className="text-xs text-slate-500">{when(r.paid_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-500">
        &quot;Not completed&quot; means the student opened checkout but never paid. Refunds are handled in
        your Razorpay dashboard.
      </p>
    </div>
  );
}
